import type { AppDispatch } from '../../../store'
import store from '../../../store'
import { Security, SecurityCategory_CategoryCode, Segment } from '../../../proto/generated/security'
import { updateMultiStrikeChartConfig } from '../../../reducer/chart.reducer'
import { updateWidgetSettings, updateWidgetType } from '../../../reducer/widgetDashboard.reducer'
import { selectActiveLayout, selectActiveLayoutId } from '../../../selectors/home'
import { selectAllFeatures, selectUserSubscriptionPlans, selectUserSubscriptionPlansData } from '../../../selectors/user'
import { selectMultiStrikeChartConfigChartType, selectMultiStrikeChartConfigCumOi, selectMultiStrikeChartConfigShowChange } from '../../../selectors/chart'
import { getDefaultMultiStrikeChartSettings } from '../../../reducer/multiStrikeChart.reducer'
import { WidgetTypes } from '../../widgetDashboard/widgetDashboard.enums'
import type { WidgetDashboardConfig } from '../../widgetDashboard/widgetDashboard.interface'
import { hasFeatureAccess } from '../../../util/accessControl'
import { getSecurityAsync, getSecurityFromCache, getSecurityKey } from '../../../util/security'
import { isExpressionMode } from '../../../util/expression'
import {
    FeatureCategory,
    FeatureSubCategory,
} from '../../adminUserRestrictions/Restrictions.constants'
import {
    getMultiStrikeChartTypeMenuValue,
    requiresMultiStrikeOiAccess,
    normalizeMultiStrikeChartType,
    resolveMultiStrikeChartTypeSelection,
    type MultiStrikeChartTypeMenuValue,
} from '../../multiStikeChart/multistrikeChartTypeHelpers'
import { orderedPaneSlots, resolvePaneSlot } from './chartLayoutCommands'
import type { ChartCommandApplyEntry } from '../../screener/aiChat/types'

type MultiStrikeChartCommand = {
    chart_type?: unknown
    type?: unknown
    multi_strike_type?: unknown
    mode?: unknown
    all_panes?: unknown
    pane?: unknown
    target_pane?: unknown
    pane_index?: unknown
}

export type MultiStrikeEligibilityReason = 'no_fo' | 'no_symbol' | 'expression' | 'multi_security'

export type MultiStrikeEligibility = {
    supported: boolean
    reason?: MultiStrikeEligibilityReason
    via_underlying?: { exchange: string; code: string; display_name?: string }
    underlying_key?: string
}

export type MultiStrikeFailureReason = 'no_fo' | 'no_symbol' | 'expression' | 'multi_security' | 'restricted' | 'invalid_value' | 'pane_not_found'

const MULTI_STRIKE_TYPE_ALIASES: Record<string, MultiStrikeChartTypeMenuValue> = {
    price: 'price',
    iv: 'iv',
    implied_volatility: 'iv',
    implied_vol: 'iv',
    imp_vol: 'iv',
    volatility: 'iv',
    oi: 'oi',
    open_interest: 'oi',
    open_int: 'oi',
    oichange: 'oiChange',
    oi_change: 'oiChange',
    straddle: 'straddle',
    oi_diff: 'oiDiff',
    oidiff: 'oiDiff',
    pcr: 'pcr',
    put_call_ratio: 'pcr',
}

export function resolveMultiStrikeChartTypeInput(value: unknown): MultiStrikeChartTypeMenuValue | undefined {
    const raw = String(value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_')
    if (!raw) return undefined
    return MULTI_STRIKE_TYPE_ALIASES[raw.replace(/_+$/, '')]
}

export function hasMultiStrikeOiAccess(): boolean {
    const state = store.getState()
    const allFeatures = selectAllFeatures(state)
    const plans = selectUserSubscriptionPlans(state)
    const plansData = selectUserSubscriptionPlansData(state)
    const userFeatures = plans?.length
        ? plans.flatMap((plan: string) => plansData?.[plan] ?? [])
        : undefined
    return hasFeatureAccess(
        allFeatures,
        userFeatures,
        FeatureCategory.MULTI_STRIKE,
        FeatureSubCategory.OI_CHARTS,
    )
}

export function buildMultiStrikeTypeCatalog(): Array<{ type: string; label: string; available: boolean; requires?: string }> {
    const oiAccess = hasMultiStrikeOiAccess()
    return [
        { type: 'price', label: 'Price', available: true },
        { type: 'iv', label: 'Imp. Vol', available: true },
        { type: 'oi', label: 'Open Int.', available: oiAccess, requires: oiAccess ? undefined : 'OI_CHARTS' },
        { type: 'oiChange', label: 'OI Change', available: oiAccess, requires: oiAccess ? undefined : 'OI_CHARTS' },
        { type: 'straddle', label: 'Straddle', available: oiAccess, requires: oiAccess ? undefined : 'OI_CHARTS' },
        { type: 'oiDiff', label: 'OI Diff', available: oiAccess, requires: oiAccess ? undefined : 'OI_CHARTS' },
        { type: 'pcr', label: 'PCR', available: oiAccess, requires: oiAccess ? undefined : 'OI_CHARTS' },
    ]
}

function isFoUnderlying(security: Security): boolean {
    if (security.hasFo) return true
    const category = security.categoryCode
    return category !== SecurityCategory_CategoryCode.STOCKS
        && category !== SecurityCategory_CategoryCode.INDEX
}

export function resolveMultiStrikeEligibility(
    key: string | undefined,
    options?: { expressionMode?: boolean; securitiesCount?: number },
): MultiStrikeEligibility {
    if (!key) {
        return { supported: false, reason: 'no_symbol' }
    }
    if (options?.expressionMode || isExpressionMode(key)) {
        return { supported: false, reason: 'expression' }
    }
    if ((options?.securitiesCount ?? 1) > 1) {
        return { supported: false, reason: 'multi_security' }
    }

    const security = getSecurityFromCache(key)
    if (!security) {
        return { supported: false, reason: 'no_symbol' }
    }

    if ([Segment.FO, Segment.CD].includes(security.segment)) {
        const underlyingCode = security.underlyingSecurityCode
        if (!underlyingCode) {
            return { supported: false, reason: 'no_symbol' }
        }
        const underlyingKey = getSecurityKey(security.exchange, underlyingCode)
        const underlying = getSecurityFromCache(underlyingKey)
        return {
            supported: true,
            underlying_key: underlyingKey,
            via_underlying: {
                exchange: String(security.exchange),
                code: underlyingCode,
                display_name: underlying?.displayName || underlying?.symbol,
            },
        }
    }

    if (
        [SecurityCategory_CategoryCode.STOCKS, SecurityCategory_CategoryCode.INDEX].includes(security.categoryCode)
        && !isFoUnderlying(security)
    ) {
        return { supported: false, reason: 'no_fo' }
    }

    return { supported: true, underlying_key: key }
}

export async function resolveMultiStrikeEligibilityAsync(
    key: string | undefined,
    options?: { expressionMode?: boolean; securitiesCount?: number },
): Promise<MultiStrikeEligibility> {
    const sync = resolveMultiStrikeEligibility(key, options)
    if (sync.reason !== 'no_symbol' || !key || options?.expressionMode || isExpressionMode(key)) {
        return sync
    }

    const security = await getSecurityAsync(key)
    if (!security) {
        return { supported: false, reason: 'no_symbol' }
    }
    window.securityManager?.setSecurity(security)
    return resolveMultiStrikeEligibility(key, options)
}

function paneLabel(widget: WidgetDashboardConfig['widgets'][number] | undefined): string {
    return widget?.settings?.displayName || widget?.settings?.key || 'chart'
}

function multiStrikeFailureMessage(reason: MultiStrikeFailureReason, label: string): string {
    switch (reason) {
        case 'no_fo':
            return `${label} has no F&O contracts, so it can't show a multistrike chart.`
        case 'no_symbol':
            return `${label} has no symbol loaded.`
        case 'expression':
            return `${label} uses an expression chart, which multistrike doesn't support.`
        case 'multi_security':
            return `${label} compares multiple symbols, which multistrike doesn't support.`
        case 'restricted':
            return `Multistrike OI charts need the OI Charts add-on. Use Price or Imp. Vol instead.`
        case 'invalid_value':
            return `That multistrike chart type isn't supported.`
        case 'pane_not_found':
            return "Couldn't find that pane."
    }
}

function isAlreadyMultiStrikeType(
    viewKey: string,
    menuValue: MultiStrikeChartTypeMenuValue,
    widgetType: WidgetTypes | undefined,
): boolean {
    if (widgetType !== WidgetTypes.MULTI_STRIKE_CHART) return false
    const state = store.getState()
    const chartType = selectMultiStrikeChartConfigChartType(viewKey)(state)
    const cumOi = selectMultiStrikeChartConfigCumOi(viewKey)(state)
    const showChange = selectMultiStrikeChartConfigShowChange(viewKey)(state)
    const currentMenuValue = getMultiStrikeChartTypeMenuValue(
        normalizeMultiStrikeChartType(chartType, showChange),
        cumOi,
        showChange,
    )
    return currentMenuValue === menuValue
}

export async function applyMultiStrikeChartToPane(params: {
    layoutId: string
    layout: WidgetDashboardConfig
    slot: number
    paneIndex: number
    menuValue: MultiStrikeChartTypeMenuValue
    dispatch: AppDispatch
}): Promise<{ status: 'applied' | 'noop' | 'failed'; message: string; reason?: MultiStrikeFailureReason }> {
    const { layoutId, layout, slot, paneIndex, menuValue, dispatch } = params
    const widget = layout.widgets?.[slot]
    const label = paneLabel(widget)
    const panePrefix = layout.panes && orderedPaneSlots(layout).length > 1 ? `Pane ${paneIndex} (${label})` : label

    const eligibility = await resolveMultiStrikeEligibilityAsync(widget?.settings?.key, {
        expressionMode: widget?.settings?.expressionMode,
        securitiesCount: widget?.settings?.securities?.length,
    })
    if (!eligibility.supported) {
        const reason = eligibility.reason ?? 'no_symbol'
        return { status: 'failed', message: multiStrikeFailureMessage(reason, panePrefix), reason }
    }

    const nextConfig = resolveMultiStrikeChartTypeSelection(menuValue, false, false)
    if (requiresMultiStrikeOiAccess(nextConfig.chartType) && !hasMultiStrikeOiAccess()) {
        return { status: 'failed', message: multiStrikeFailureMessage('restricted', panePrefix), reason: 'restricted' }
    }

    const viewKey = widget?.settings?.chartId ?? `${layoutId}-${slot}`
    if (isAlreadyMultiStrikeType(viewKey, menuValue, widget?.widgetType)) {
        return { status: 'noop', message: `${panePrefix} is already multistrike ${menuValue}` }
    }

    const underlyingKey = eligibility.underlying_key ?? widget?.settings?.key
    if (!underlyingKey) {
        return { status: 'failed', message: multiStrikeFailureMessage('no_symbol', panePrefix), reason: 'no_symbol' }
    }

    const isExistingMultiStrike = widget?.widgetType === WidgetTypes.MULTI_STRIKE_CHART
    dispatch(updateMultiStrikeChartConfig({
        viewKey,
        config: isExistingMultiStrike
            ? nextConfig
            : getDefaultMultiStrikeChartSettings({
                ...nextConfig,
                key: underlyingKey,
            }),
    }))

    if (underlyingKey !== widget?.settings?.key) {
        const underlying = getSecurityFromCache(underlyingKey)
        dispatch(updateWidgetSettings({
            id: layoutId,
            slotId: slot,
            settings: {
                key: underlyingKey,
                expressionMode: false,
                securities: [underlyingKey],
                displayName: underlying?.displayName || underlying?.symbol || label,
            },
        }))
    }

    if (widget?.widgetType !== WidgetTypes.MULTI_STRIKE_CHART) {
        dispatch(updateWidgetType({
            id: layoutId,
            slotId: slot,
            type: WidgetTypes.MULTI_STRIKE_CHART,
        }))
    }

    const typeLabel = menuValue === 'iv' ? 'Imp. Vol' : menuValue
    return {
        status: 'applied',
        message: `Set ${panePrefix} to multistrike ${typeLabel}`,
    }
}

export async function applyMultiStrikeChartCommand(
    command: MultiStrikeChartCommand,
    dispatch: AppDispatch,
): Promise<{ applied: ChartCommandApplyEntry[]; failed: ChartCommandApplyEntry[] }> {
    const applied: ChartCommandApplyEntry[] = []
    const failed: ChartCommandApplyEntry[] = []
    const menuValue = resolveMultiStrikeChartTypeInput(
        command.chart_type ?? command.type ?? command.multi_strike_type ?? command.mode,
    )
    if (!menuValue) {
        failed.push({
            op: 'set_multistrike_chart',
            message: multiStrikeFailureMessage('invalid_value', 'Chart'),
            reason: 'invalid_value',
        })
        return { applied, failed }
    }

    const state = store.getState()
    const layoutId = selectActiveLayoutId(state)
    const layout = selectActiveLayout(state)
    if (!layoutId || !layout) {
        failed.push({
            op: 'set_multistrike_chart',
            message: "The chart layout isn't ready yet. Try again in a moment.",
            reason: 'layout_unavailable',
        })
        return { applied, failed }
    }

    const allPanes = command.all_panes === true || String(command.all_panes).toLowerCase() === 'true'
    const slots = orderedPaneSlots(layout)
    const targetSlots = allPanes
        ? slots
        : (() => {
            const slot = resolvePaneSlot(layout, command.pane ?? command.target_pane ?? command.pane_index)
            return slot != null ? [slot] : []
        })()

    if (!targetSlots.length) {
        failed.push({
            op: 'set_multistrike_chart',
            message: multiStrikeFailureMessage('pane_not_found', 'Chart'),
            reason: 'pane_not_found',
        })
        return { applied, failed }
    }

    for (const slot of targetSlots) {
        const paneIndex = slots.indexOf(slot) + 1
        const outcome = await applyMultiStrikeChartToPane({
            layoutId,
            layout,
            slot,
            paneIndex,
            menuValue,
            dispatch,
        })
        const entry: ChartCommandApplyEntry = {
            op: 'set_multistrike_chart',
            message: outcome.message,
            reason: outcome.reason,
        }
        if (outcome.status === 'failed') {
            failed.push(entry)
        } else if (outcome.status === 'applied') {
            applied.push(entry)
        }
    }

    return { applied, failed }
}

export function buildPaneMultiStrikeContext(
    key: string | undefined,
    widgetType: string | undefined,
    viewKey: string,
    options?: { expressionMode?: boolean; securitiesCount?: number },
): {
    supported: boolean
    reason?: MultiStrikeEligibilityReason
    via_underlying?: { exchange: string; code: string; display_name?: string }
    current_type?: string
} {
    const eligibility = resolveMultiStrikeEligibility(key, options)
    const row: {
        supported: boolean
        reason?: MultiStrikeEligibilityReason
        via_underlying?: { exchange: string; code: string; display_name?: string }
        current_type?: string
    } = {
        supported: eligibility.supported,
        ...(eligibility.reason ? { reason: eligibility.reason } : {}),
        ...(eligibility.via_underlying ? { via_underlying: eligibility.via_underlying } : {}),
    }

    if (widgetType === WidgetTypes.MULTI_STRIKE_CHART) {
        const state = store.getState()
        const chartType = selectMultiStrikeChartConfigChartType(viewKey)(state)
        const cumOi = selectMultiStrikeChartConfigCumOi(viewKey)(state)
        const showChange = selectMultiStrikeChartConfigShowChange(viewKey)(state)
        row.current_type = getMultiStrikeChartTypeMenuValue(
            normalizeMultiStrikeChartType(chartType, showChange),
            cumOi,
            showChange,
        )
    }

    return row
}
